import { useEffect, useState } from "react";
import { X, Coins, ArrowUpRight, ArrowDownRight } from "lucide-react";
import { gameApi } from "@/lib/api";

const fmt = (n) => (n ?? 0).toLocaleString();
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

function metaLabel(meta) {
  if (!meta) return "";
  const parts = [];
  if (meta.item) parts.push(meta.item + (meta.qty ? ` ×${meta.qty}` : ""));
  if (meta.horse) parts.push(meta.horse);
  if (meta.barn) parts.push(meta.barn);
  if (meta.chore) parts.push(meta.chore);
  if (meta.show) parts.push(meta.show);
  if (meta.goal) parts.push(meta.goal);
  if (meta.discipline) parts.push(meta.discipline);
  if (meta.placing) parts.push(`#${meta.placing} place`);
  return parts.length ? " · " + parts.join(" · ") : "";
}

// Reusable coin-transaction ledger modal. Player mode by default; pass a
// playerNumber to view any player's history through the admin endpoint.
export const CoinHistory = ({ open, onClose, playerNumber = null }) => {
  const [rows, setRows] = useState([]);
  const [balance, setBalance] = useState(null);
  const [header, setHeader] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const url = playerNumber != null
          ? `/game/admin/players/${playerNumber}/coin-ledger`
          : "/game/coin-ledger";
        const { data } = await gameApi.get(url);
        if (!alive) return;
        setRows(data.transactions || []);
        setBalance(data.currency ?? data.player?.currency ?? null);
        setHeader(data.player || null);
      } catch (e) {
        if (alive) { setRows([]); setBalance(null); setHeader(null); }
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, playerNumber]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[88] flex items-center justify-center bg-black/60 p-4"
      data-testid="coin-history-modal" onClick={onClose}>
      <div className="flex max-h-[80vh] w-full max-w-md flex-col rounded-2xl border border-border bg-card"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <h3 className="font-display text-lg font-bold text-primary">Coin History</h3>
            {header && <p className="text-xs text-muted-foreground">#{header.number} · {header.name}</p>}
          </div>
          <div className="flex items-center gap-3">
            {balance != null && (
              <span className="flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-sm font-bold text-amber-700">
                <Coins size={14} /> {fmt(balance)}
              </span>
            )}
            <button data-testid="coin-history-close" onClick={onClose} className="rounded-full p-1 hover:bg-black/5"><X size={18} /></button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <p className="py-10 text-center text-sm text-muted-foreground">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground" data-testid="coin-history-empty">
              No transactions yet. Your coin activity — prizes, purchases, barn chores and more — will show up here.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {rows.map((r, i) => {
                const pos = (r.amount ?? 0) >= 0;
                return (
                  <li key={r.id || i} data-testid="coin-history-row"
                    className="flex items-center gap-3 rounded-xl border border-border bg-background px-3 py-2">
                    <div className={`grid h-8 w-8 shrink-0 place-items-center rounded-full ${pos ? "bg-green-100 text-green-700" : "bg-rose-100 text-rose-600"}`}>
                      {pos ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{r.source}</p>
                      <p className="text-[11px] text-muted-foreground">{fmtDate(r.at)}{metaLabel(r.meta)}</p>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className={`text-sm font-bold ${pos ? "text-green-700" : "text-rose-600"}`}>{pos ? "+" : ""}{fmt(r.amount)}🪙</p>
                      {r.balance_after != null && <p className="text-[11px] text-muted-foreground">bal {fmt(r.balance_after)}</p>}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};
